import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  IndianRupee, Receipt, Banknote, FileText, CheckCircle2, XCircle,
  Sparkles, ExternalLink, RefreshCw, Bot, Wallet, Hourglass,
} from 'lucide-react';

const CATEGORY_COLOURS = {
  Equipment: 'bg-amber-50 text-amber-700 border-amber-200',
  Travel: 'bg-sky-50 text-sky-700 border-sky-200',
  Medical: 'bg-rose-50 text-rose-700 border-rose-200',
  Facility: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Other: 'bg-slate-50 text-slate-700 border-slate-200',
};

function formatINR(n) {
  if (n === undefined || n === null) return '\u20B90';
  const v = Number(n) || 0;
  return v.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch (_) { return ''; }
}

function MetricTile({ icon: Icon, label, value, sub, tone = 'emerald', testId }) {
  const toneMap = {
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-900',
    indigo: 'from-indigo-50 to-white border-indigo-200 text-indigo-900',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-900',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-900',
  };
  return (
    <div className={`bg-gradient-to-br ${toneMap[tone]} border rounded-2xl px-5 py-4 shadow-sm`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider opacity-75">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-xs opacity-70">{sub}</div> : null}
    </div>
  );
}

function CollectionRow({ row }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/40 transition-colors border-b last:border-b-0" data-testid={`collection-row-${row.fee_id}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center flex-shrink-0">
        <Banknote className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">
          {row.student_name}
          <span className="text-slate-500 font-normal"> · {row.month || '\u2014'}</span>
        </div>
        <div className="text-xs text-slate-500 truncate">
          {row.parent_name} · {timeAgo(row.paid_at)} · {row.payment_id.slice(-10)}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-bold text-emerald-700 tabular-nums">{formatINR(row.amount)}</div>
        {row.has_pdf ? (
          <a
            href={row.download_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 mt-0.5"
            data-testid={`collection-pdf-${row.fee_id}`}
          >
            <FileText className="w-3 h-3" /> Receipt
          </a>
        ) : (
          <Badge variant="outline" className="text-[10px] mt-0.5">No PDF</Badge>
        )}
      </div>
    </div>
  );
}

function ExpenseRow({ row, onReview }) {
  const categoryClass = CATEGORY_COLOURS[row.category] || CATEGORY_COLOURS.Other;
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-indigo-50/40 transition-colors border-b last:border-b-0" data-testid={`expense-row-${row.id}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
        <Receipt className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
          {row.vendor}
          <Badge variant="outline" className={`text-[10px] ${categoryClass}`}>{row.category}</Badge>
        </div>
        <div className="text-xs text-slate-500 truncate">
          {row.coach_name || 'Coach'} · {row.date} · <Bot className="w-3 h-3 inline" /> AI-scanned
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-bold text-indigo-700 tabular-nums">{formatINR(row.amount)}</div>
        <Button
          size="sm"
          className="mt-1 h-7 px-3 text-xs bg-slate-900 hover:bg-slate-700 text-white"
          onClick={() => onReview(row)}
          data-testid={`expense-review-${row.id}`}
        >
          Review
        </Button>
      </div>
    </div>
  );
}

function ApprovalDialog({ open, expense, onClose, onApprove, onReject }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !expense?.id) { setDetail(null); return; }
    setNote('');
    setBusy(false);
    setDetail(null);
    (async () => {
      try {
        const { data } = await api.get(`/finance/expenses/${expense.id}`);
        setDetail(data);
      } catch (e) {
        // Detail load can fail without blocking the action — list row has the basics.
      }
    })();
  }, [open, expense]);

  if (!open || !expense) return null;
  const imgSrc = detail?.image_base64
    ? `data:${detail.image_mime || 'image/jpeg'};base64,${detail.image_base64}`
    : null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="expense-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-indigo-600" />
            Review AI-scanned expense
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
          <div>
            {imgSrc ? (
              <img
                src={imgSrc}
                alt="Receipt"
                className="w-full rounded-lg border border-slate-200 max-h-96 object-contain bg-slate-50"
                data-testid="expense-image-preview"
              />
            ) : (
              <div className="w-full h-64 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-sm text-slate-400">
                <Skeleton className="w-full h-full" />
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">AI Extracted</div>
              <div className="mt-2 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Vendor</span><span className="font-semibold">{expense.vendor}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold text-indigo-700">{formatINR(expense.amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date</span><span>{expense.date}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Category</span><Badge className={CATEGORY_COLOURS[expense.category] || CATEGORY_COLOURS.Other}>{expense.category}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-500">Submitted by</span><span>{expense.coach_name || 'Coach'}</span></div>
              </div>
            </div>
            <div>
              <label htmlFor="approval-note" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Note (optional)</label>
              <Textarea
                id="approval-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Add an internal note for the audit log"
                className="mt-1 text-sm"
                data-testid="approval-note-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={async () => { setBusy(true); await onApprove(expense.id, note); setBusy(false); }}
                disabled={busy}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="approve-expense-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button
                onClick={async () => { setBusy(true); await onReject(expense.id, note); setBusy(false); }}
                disabled={busy}
                variant="outline"
                className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="reject-expense-btn"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FinanceAutopilotDashboard() {
  const [summary, setSummary] = useState(null);
  const [collections, setCollections] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c, e] = await Promise.all([
        api.get('/finance/summary'),
        api.get('/finance/collections?limit=50'),
        api.get('/finance/expenses?status=pending_approval&limit=100'),
      ]);
      setSummary(s.data);
      setCollections(c.data.collections || []);
      setExpenses(e.data.expenses || []);
    } catch (err) {
      toast.error('Failed to load Finance Autopilot data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 30s for near-real-time updates on the collections feed.
  useEffect(() => {
    const t = setInterval(() => { setRefreshing(true); load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const handleApprove = async (id, note) => {
    try {
      await api.post(`/finance/expenses/${id}/approve`, { note: note || null });
      toast.success('Expense approved');
      setReviewing(null);
      load();
    } catch (err) {
      toast.error('Failed to approve expense');
    }
  };

  const handleReject = async (id, note) => {
    try {
      await api.post(`/finance/expenses/${id}/reject`, { note: note || null });
      toast.success('Expense rejected');
      setReviewing(null);
      load();
    } catch (err) {
      toast.error('Failed to reject expense');
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-4" data-testid="finance-autopilot-loading">
        <Skeleton className="h-9 w-72" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto" data-testid="finance-autopilot-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-emerald-600" />
            Finance Autopilot
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            UPI AutoPay collections + AI-scanned expense receipts, fully automated.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          data-testid="finance-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Metric Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricTile
          icon={IndianRupee}
          label="Collected MTD"
          value={formatINR(summary?.mtd_collected)}
          sub={`${summary?.mtd_collected_count || 0} payments · ${summary?.autopay_count || 0} via AutoPay`}
          tone="emerald"
          testId="metric-mtd-collected"
        />
        <MetricTile
          icon={Hourglass}
          label="Pending Approvals"
          value={summary?.pending_expenses_count || 0}
          sub={formatINR(summary?.pending_expenses_value)}
          tone="amber"
          testId="metric-pending-expenses"
        />
        <MetricTile
          icon={Receipt}
          label="Expenses Approved MTD"
          value={formatINR(summary?.approved_expenses_value_mtd)}
          tone="indigo"
          testId="metric-approved-expenses"
        />
        <MetricTile
          icon={Wallet}
          label="Net MTD"
          value={formatINR(summary?.mtd_net)}
          sub="Collected − Approved expenses"
          tone="slate"
          testId="metric-mtd-net"
        />
      </div>

      {/* Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: Inbound AutoPay feed */}
        <Card className="border-emerald-200/60 shadow-sm" data-testid="collections-panel">
          <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-white pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Banknote className="w-4 h-4 text-emerald-600" />
              AutoPay Collections
              <Badge variant="outline" className="ml-auto bg-white text-emerald-700 border-emerald-300 text-xs">
                {collections.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {collections.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="no-collections">
                <Banknote className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                No AutoPay collections yet. Enable <strong>UPI AutoPay</strong> in Settings to start.
              </div>
            ) : (
              collections.map(row => <CollectionRow key={row.fee_id} row={row} />)
            )}
          </CardContent>
        </Card>

        {/* Right: AI-scanned expenses awaiting approval */}
        <Card className="border-indigo-200/60 shadow-sm" data-testid="expenses-panel">
          <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-white pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="w-4 h-4 text-indigo-600" />
              AI-Scanned Expenses
              <Badge variant="outline" className="ml-auto bg-white text-indigo-700 border-indigo-300 text-xs">
                {expenses.length} pending
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {expenses.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="no-expenses">
                <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                No expenses awaiting approval. Coaches snap a receipt photo on WhatsApp and the AI logs them here.
              </div>
            ) : (
              expenses.map(row => <ExpenseRow key={row.id} row={row} onReview={setReviewing} />)
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer hint */}
      <div className="text-center text-xs text-slate-400 pt-4 pb-2">
        <ExternalLink className="w-3 h-3 inline mr-1" />
        Powered by Razorpay AutoPay + Claude 3.5 Sonnet Vision.
        Toggle features in <strong>Settings → Automation Flags</strong>.
      </div>

      <ApprovalDialog
        open={!!reviewing}
        expense={reviewing}
        onClose={() => setReviewing(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
